import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Switch } from '@/components/ui/Switch';
import { toast } from '@/hooks/useToast';
import { getBranches, getSpecialties } from '../api';
import type { Branch, Specialty } from '../types';
import { SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';
import {
  addOpdFeeOverride, createOpdFeeItem, deleteOpdFeeOverride, getOpdFeeItems, updateOpdFeeItem, updateOpdFeeOverride,
} from '@/features/opd-fees/api';
import {
  APPLIES_TO_LABELS, CATEGORY_LABELS,
  type OpdFeeAppliesTo, type OpdFeeCategory, type OpdFeeItem, type OpdFeeItemInput,
} from '@/features/opd-fees/types';

const rupees = (v: string | number) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Admin → Billing & Insurance → OPD Fee Schedule: what the OPD counter charges
 * (registration, consultation, procedure, other), the default fee for each,
 * and fees that differ per branch or department. A new OPD bill — in the visit
 * pipeline's Billing stage or the Billing screen — starts from these.
 *
 * Changing a fee never changes a bill already generated (bills keep their own
 * copy of each line). Items are deactivated, never deleted. Every change is
 * written to the audit log.
 */
export default function OpdFeeScheduleTab() {
  const [items, setItems] = useState<OpdFeeItem[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<OpdFeeItem | null>(null);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);

  const load = useCallback(
    () => getOpdFeeItems().then((r) => { setItems(r); setLoadError(''); }).catch((e: Error) => setLoadError(e.message)),
    []
  );
  useEffect(() => {
    load();
    getBranches().then(setBranches).catch(() => setBranches([]));
    getSpecialties(true).then(setSpecialties).catch(() => setSpecialties([]));
  }, [load]);

  const act = async (fn: () => Promise<unknown>, ok: string) => {
    try { await fn(); toast({ title: ok, variant: 'success' }); await load(); }
    catch (e) { toast({ title: 'Not saved', description: e instanceof Error ? e.message : String(e), variant: 'danger' }); }
  };

  const rows = useMemo(
    () => filterByStatus(items ?? [], status).filter((i) => matchesSearch(search, i.name, i.code, CATEGORY_LABELS[i.category])),
    [items, status, search]
  );
  const editing = editingId === 'new' ? 'new' : items?.find((i) => i.id === editingId) ?? null;

  const columns: DataTableColumn<OpdFeeItem>[] = [
    { key: 'name', header: 'Item', sortable: true, sortValue: (i) => i.sort_order * 100000 + i.id,
      render: (i) => (
        <div>
          <div className="font-semibold">{i.name}</div>
          <div className="font-mono text-[11px] text-ink-muted">{i.code}</div>
        </div>
      ) },
    { key: 'category', header: 'Category', render: (i) => CATEGORY_LABELS[i.category] },
    { key: 'fee', header: 'Default fee', align: 'right', sortable: true, sortValue: (i) => Number(i.default_fee),
      render: (i) => (
        <div>
          <div className="font-semibold tabular-nums">{rupees(i.default_fee)}</div>
          {Number(i.gst_percent) > 0 && <div className="text-[11px] text-ink-muted">+ {Number(i.gst_percent)}% GST</div>}
        </div>
      ) },
    { key: 'overrides', header: 'Branch / department fees',
      render: (i) => {
        const active = i.fees.filter((f) => f.active);
        return active.length ? `${active.length} different fee${active.length === 1 ? '' : 's'}` : <span className="text-ink-muted">Same everywhere</span>;
      } },
    { key: 'added', header: 'Added to bill',
      render: (i) => (i.auto_add ? APPLIES_TO_LABELS[i.applies_to] : <span className="text-ink-muted">Only when picked</span>) },
    { key: 'discount', header: 'Discount limit', align: 'right',
      render: (i) => (Number(i.max_discount_percent) > 0 ? `${Number(i.max_discount_percent)}%` : <span className="text-ink-muted">None</span>) },
    { key: 'status', header: 'Status', widthClassName: 'w-[110px]',
      render: (i) => <Badge variant={i.active ? 'success' : 'neutral'}>{i.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (i) => (
        <RowActionsMenu
          label={`Actions for ${i.name}`}
          actions={[
            { label: 'Edit', onSelect: () => setEditingId(i.id) },
            i.active
              ? { label: 'Deactivate…', danger: true, onSelect: () => setConfirmDeactivate(i) }
              : { label: 'Reactivate', onSelect: () => act(() => updateOpdFeeItem(i.id, { active: true }), `${i.name} reactivated`) },
          ]}
        />
      ) },
  ];

  return (
    <div>
      <p className="mb-3 max-w-3xl text-[12.5px] text-ink-muted">
        The charges a new OPD bill starts with. Billers without the <b>change OPD fees</b> permission
        (Users &amp; Roles) bill these fees as set here and can give a discount only up to each item&apos;s limit.
        Changing a fee does not change bills already generated.
      </p>
      <SettingsToolbar
        search={search}
        onSearch={setSearch}
        placeholder="Search items"
        status={status}
        onStatus={setStatus}
        count={items ? `${rows.length} item${rows.length === 1 ? '' : 's'}` : undefined}
        action={<Button onClick={() => setEditingId('new')}><Plus className="h-4 w-4" /> Add item</Button>}
      />
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(i) => i.id}
        isLoading={!items && !loadError}
        isError={!!loadError}
        errorMessage={loadError}
        onRetry={load}
        initialSort={{ key: 'name', direction: 'asc' }}
        emptyLabel={search ? 'No items match' : status === 'inactive' ? 'No inactive items' : 'No billing items yet'}
      />

      <OpdFeeItemSheet
        item={editing}
        branches={branches}
        specialties={specialties}
        onClose={() => setEditingId(null)}
        onSaved={async (msg, created) => {
          toast({ title: msg, variant: 'success' });
          await load();
          // A new item stays open so its branch/department fees can be added straight away.
          setEditingId(created ? created.id : null);
        }}
        onFeesChanged={load}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.name ?? ''}?`}
        description="It will no longer be offered on new OPD bills. Bills already generated keep it unchanged, and you can reactivate it later."
        confirmLabel="Deactivate"
        onConfirm={() => { const i = confirmDeactivate; if (i) act(() => updateOpdFeeItem(i.id, { active: false }), `${i.name} deactivated`); }}
      />
    </div>
  );
}

interface FormState {
  code: string;
  name: string;
  category: OpdFeeCategory;
  default_fee: string;
  gst_percent: string;
  hsn_code: string;
  applies_to: OpdFeeAppliesTo;
  auto_add: boolean;
  max_discount_percent: string;
  sort_order: string;
}

const EMPTY: FormState = {
  code: '', name: '', category: 'consultation', default_fee: '', gst_percent: '0', hsn_code: '999319',
  applies_to: 'all', auto_add: true, max_discount_percent: '0', sort_order: '100',
};

function OpdFeeItemSheet({ item, branches, specialties, onClose, onSaved, onFeesChanged }: {
  item: OpdFeeItem | 'new' | null;
  branches: Branch[];
  specialties: Specialty[];
  onClose: () => void;
  onSaved: (msg: string, created?: OpdFeeItem) => void;
  onFeesChanged: () => void;
}) {
  const isNew = item === 'new';
  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const current = item && item !== 'new' ? item : null;
  const itemId = current?.id ?? null;

  useEffect(() => {
    if (!item) return;
    setTouched(false); setError('');
    setForm(item === 'new' ? EMPTY : {
      code: item.code, name: item.name, category: item.category, default_fee: String(Number(item.default_fee)),
      gst_percent: String(Number(item.gst_percent)), hsn_code: item.hsn_code, applies_to: item.applies_to,
      auto_add: item.auto_add, max_discount_percent: String(Number(item.max_discount_percent)), sort_order: String(item.sort_order),
    });
    // Reset only when a different item is opened, not on every list refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, isNew, !!item]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((p) => ({ ...p, [k]: v }));
  const num = (v: string) => v.trim() !== '' && Number.isFinite(Number(v));
  const errors = {
    code: isNew && !/^[A-Za-z0-9_-]{1,30}$/.test(form.code.trim()) ? 'Letters, digits, - and _ (max 30)' : undefined,
    name: !form.name.trim() ? 'Enter a name' : undefined,
    default_fee: !num(form.default_fee) || Number(form.default_fee) < 0 ? 'Enter a fee, 0 or more' : undefined,
    gst_percent: !num(form.gst_percent) || Number(form.gst_percent) < 0 || Number(form.gst_percent) > 100 ? '0 to 100' : undefined,
    max_discount_percent: !num(form.max_discount_percent) || Number(form.max_discount_percent) < 0 || Number(form.max_discount_percent) > 100 ? '0 to 100' : undefined,
    sort_order: !/^\d+$/.test(form.sort_order.trim()) ? 'A whole number' : undefined,
  };
  const hasErrors = Object.values(errors).some(Boolean);

  const save = async () => {
    setTouched(true);
    if (hasErrors) return;
    const body: OpdFeeItemInput = {
      name: form.name.trim(), category: form.category, default_fee: Number(form.default_fee),
      gst_percent: Number(form.gst_percent), hsn_code: form.hsn_code.trim() || '999319', applies_to: form.applies_to,
      auto_add: form.auto_add, max_discount_percent: Number(form.max_discount_percent), sort_order: Number(form.sort_order),
    };
    setSaving(true); setError('');
    try {
      if (isNew) {
        const created = await createOpdFeeItem({ ...body, code: form.code.trim().toUpperCase() });
        onSaved(`${created.name} added`, created);
      } else if (itemId) {
        await updateOpdFeeItem(itemId, body);
        onSaved(`${body.name} saved`);
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setSaving(false);
  };

  return (
    <Sheet open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="sm:max-w-[560px]">
        <SheetHeader>
          <SheetTitle>{current ? `Edit ${current.name}` : 'Add billing item'}</SheetTitle>
          <SheetDescription>Bills already generated are not affected by changes here.</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Name" required error={touched ? errors.name : undefined} className="sm:col-span-2">
              <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Consultation" autoFocus />
            </FormField>
            <FormField label="Code" required error={touched ? errors.code : undefined} hint={isNew ? 'Cannot be changed later' : undefined}>
              <Input value={form.code} disabled={!isNew} onChange={(e) => set('code', e.target.value.toUpperCase())} placeholder="CONSULT" className="font-mono" />
            </FormField>
            <FormField label="Category" hint="Decides the Day Book department">
              <NativeSelect value={form.category} onChange={(e) => set('category', e.target.value as OpdFeeCategory)}>
                {(Object.keys(CATEGORY_LABELS) as OpdFeeCategory[]).map((c) => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
              </NativeSelect>
            </FormField>
            <FormField label="Default fee (₹)" required error={touched ? errors.default_fee : undefined} hint="Charged unless a branch or department fee below applies">
              <Input type="number" min="0" inputMode="decimal" value={form.default_fee} onChange={(e) => set('default_fee', e.target.value)} />
            </FormField>
            <FormField label="GST %" error={touched ? errors.gst_percent : undefined} hint="Most OPD services are GST-exempt (0%)">
              <Input type="number" min="0" max="100" inputMode="decimal" value={form.gst_percent} onChange={(e) => set('gst_percent', e.target.value)} />
            </FormField>
            <FormField label="HSN / SAC code">
              <Input value={form.hsn_code} onChange={(e) => set('hsn_code', e.target.value)} className="font-mono" />
            </FormField>
            <FormField label="Discount limit %" error={touched ? errors.max_discount_percent : undefined} hint="Most a biller without fee-override permission may give">
              <Input type="number" min="0" max="100" inputMode="decimal" value={form.max_discount_percent} onChange={(e) => set('max_discount_percent', e.target.value)} />
            </FormField>
            <FormField label="Added to a new bill" className="sm:col-span-2">
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-[13px]">
                  <Switch checked={form.auto_add} onCheckedChange={(v) => set('auto_add', v)} aria-label="Add automatically" />
                  Automatically
                </label>
                {form.auto_add ? (
                  <NativeSelect className="w-auto" value={form.applies_to} onChange={(e) => set('applies_to', e.target.value as OpdFeeAppliesTo)}>
                    {(Object.keys(APPLIES_TO_LABELS) as OpdFeeAppliesTo[]).map((a) => <option key={a} value={a}>{APPLIES_TO_LABELS[a]}</option>)}
                  </NativeSelect>
                ) : (
                  <span className="text-[12px] text-ink-muted">Billers add it with one click when needed</span>
                )}
              </div>
            </FormField>
            <FormField label="Order on the bill" error={touched ? errors.sort_order : undefined} hint="Lower numbers come first">
              <Input type="number" min="0" value={form.sort_order} onChange={(e) => set('sort_order', e.target.value)} />
            </FormField>
          </div>

          {current && (
            <FeeOverrides item={current} branches={branches} specialties={specialties} onChanged={onFeesChanged} />
          )}
          {isNew && (
            <div className="rounded-md bg-canvas px-3 py-2 text-[12px] text-ink-muted">
              After adding the item you can set different fees for particular branches or departments.
            </div>
          )}
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add item' : 'Save'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/** The item's branch/department fees. Each add/change/remove saves straight away. */
function FeeOverrides({ item, branches, specialties, onChanged }: {
  item: OpdFeeItem;
  branches: Branch[];
  specialties: Specialty[];
  onChanged: () => void;
}) {
  const [branchId, setBranchId] = useState('');
  const [specialtyId, setSpecialtyId] = useState('');
  const [fee, setFee] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError('');
    try { await fn(); onChanged(); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setBusy(false);
  };

  const add = () => {
    if (!branchId && !specialtyId) { setError('Pick a branch, a department, or both'); return; }
    if (fee.trim() === '' || !Number.isFinite(Number(fee)) || Number(fee) < 0) { setError('Enter a fee, 0 or more'); return; }
    run(async () => {
      await addOpdFeeOverride(item.id, { branch_id: branchId ? Number(branchId) : null, specialty_id: specialtyId ? Number(specialtyId) : null, fee: Number(fee) });
      setBranchId(''); setSpecialtyId(''); setFee('');
    });
  };

  return (
    <div className="mt-2 border-t border-line pt-3">
      <div className="mb-1 text-[13px] font-semibold">Fees for particular branches or departments</div>
      <p className="mb-2 text-[11.5px] text-ink-muted">
        The most specific fee wins: branch + department, then department, then branch, then the default fee ({rupees(item.default_fee)}).
      </p>
      {error && <div className="mb-2 rounded-md bg-danger-soft px-3 py-1.5 text-[12px] text-danger-ink">{error}</div>}
      {item.fees.length === 0 ? (
        <div className="mb-2 text-[12px] text-ink-muted">None — the default fee applies everywhere.</div>
      ) : (
        <div className="mb-2 divide-y divide-line rounded-md border border-line">
          {item.fees.map((f) => (
            <div key={f.id} className="flex items-center gap-2 px-2.5 py-1.5 text-[12.5px]">
              <div className="min-w-0 flex-1">
                <div className="truncate font-medium">
                  {f.branch_name || 'Every branch'} · {f.specialty_name || 'Every department'}
                </div>
              </div>
              <Input
                key={`${f.id}-${f.fee}`}
                aria-label="Fee" type="number" min="0" className="w-[110px]" defaultValue={Number(f.fee)} disabled={busy}
                onBlur={(e) => { const v = Number(e.target.value); if (e.target.value !== '' && v >= 0 && v !== Number(f.fee)) run(() => updateOpdFeeOverride(f.id, { fee: v })); }}
              />
              <Switch checked={f.active} disabled={busy} aria-label="Fee in use" onCheckedChange={(v) => run(() => updateOpdFeeOverride(f.id, { active: v }))} />
              <Button variant="ghost" size="icon" aria-label="Remove this fee" disabled={busy} onClick={() => run(() => deleteOpdFeeOverride(f.id))}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_100px_auto] sm:items-end">
        <FormField label="Branch">
          <NativeSelect value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value="">Every branch</option>
            {branches.filter((b) => b.active).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Department">
          <NativeSelect value={specialtyId} onChange={(e) => setSpecialtyId(e.target.value)}>
            <option value="">Every department</option>
            {specialties.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </NativeSelect>
        </FormField>
        <FormField label="Fee (₹)">
          <Input type="number" min="0" inputMode="decimal" value={fee} onChange={(e) => setFee(e.target.value)} />
        </FormField>
        <Button variant="ghost" onClick={add} loading={busy}><Plus className="h-4 w-4" /> Add fee</Button>
      </div>
    </div>
  );
}
